// ⚡️ CHAT UTILITIES: Constants and utility functions for chat functionality
// Extracted from ChatPanel.js for better organization and reusability

// Security: Enhanced file type validation constants
export const ALLOWED_FILE_TYPES = [
  'image/jpeg',
  'image/jpg', 
  'image/png',
  'image/gif',
  'image/webp',
  'video/mp4',
  'video/webm',
  'video/ogg'
];

export const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
export const MAX_MESSAGE_LENGTH = 1000; // Maximum message length
export const RATE_LIMIT_WINDOW = 60000; // 1 minute
export const MAX_MESSAGES_PER_MINUTE = 50; // Increased from 10 to 50 messages per minute
export const MAX_SEEN = 5000; // Maximum number of seen message IDs to keep in memory

// ⚡️ TIMESTAMP NORMALIZATION: Handle different timestamp formats consistently
export function tsToMs(t) {
  if (!t) return 0;
  if (typeof t.toDate === 'function') return t.toDate().getTime(); // Firestore Timestamp
  if (t instanceof Date) return t.getTime();                       // JS Date
  if (typeof t === 'number') return t;                             // already ms
  if (typeof t.seconds === 'number') return t.seconds * 1000;      // {seconds, nanoseconds}
  return 0;
}

// 🔧 FINAL DEDUPLICATION: Guarantee no duplicates in final message list
export function uniqueByKey(messages) {
  const seen = new Set();
  const out = [];
  
  for (const m of messages) {
    // Priority order for stable keys
    const key = m.clientMessageId
      || m.telegramMessageId
      || m.id
      || `${m.from}|${Math.round(tsToMs(m.timestamp ?? m.createdAt ?? m.clientCreatedAt)/5000)}|${(typeof m.text === 'string' ? m.text : (m.text?.sanitized || '')).substring(0, 50)}|${m.source || ''}`;
    
    if (!seen.has(key)) {
      seen.add(key);
      out.push(m);
    } else {
      // Duplicate message removed
    }
  }
  
  return out;
}

// Security: Enhanced file validation with additional checks
export const validateFile = (file) => {
  if (!file) return { isValid: false, error: 'No file provided' };
  
  // Basic client-side validation (will be double-checked server-side)
  if (!ALLOWED_FILE_TYPES.includes(file.type)) {
    return { 
      isValid: false, 
      error: `File type ${file.type} is not allowed. Allowed types: ${ALLOWED_FILE_TYPES.join(', ')}` 
    };
  }
  
  if (file.size > MAX_FILE_SIZE) {
    return { 
      isValid: false, 
      error: `File size ${(file.size / 1024 / 1024).toFixed(2)}MB exceeds maximum allowed size of ${(MAX_FILE_SIZE / 1024 / 1024)}MB` 
    };
  }
  
  // Check file name for malicious extensions
  const fileName = file.name.toLowerCase();
  const dangerousExtensions = ['.exe', '.bat', '.cmd', '.com', '.pif', '.scr', '.vbs', '.js', '.jar'];
  if (dangerousExtensions.some(ext => fileName.includes(ext))) {
    return { 
      isValid: false, 
      error: 'File type not allowed for security reasons' 
    };
  }
  
  if (file.size === 0) {
    return { 
      isValid: false, 
      error: 'File is empty' 
    };
  }
  
  return { isValid: true, error: null };
};

// Security: Input validation function (basic client-side, server-side will do thorough validation)
export const validateInput = (input) => {
  if (!input || typeof input !== 'string') return { isValid: false, error: 'Invalid input' };
  
  const trimmed = input.trim();
  if (trimmed.length === 0) return { isValid: false, error: 'Message cannot be empty' };
  if (trimmed.length > MAX_MESSAGE_LENGTH) return { isValid: false, error: `Message too long. Maximum ${MAX_MESSAGE_LENGTH} characters.` };
  
  return { isValid: true, error: null };
};

// Security: Emoji validation
export const validateEmoji = (emoji) => {
  if (!emoji || typeof emoji !== 'string') return false;
  
  // Simple validation: allow common emoji characters
  // Check if the emoji contains only valid Unicode emoji characters
  const emojiRegex = /[\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{1F1E0}-\u{1F1FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}\u{FE00}-\u{FE0F}\u{1F900}-\u{1F9FF}\u{1F018}-\u{1F270}\u{238C}-\u{23CF}\u{23E9}-\u{23F3}\u{23F8}-\u{23FA}]/u;
  
  // Also allow basic emoticons and symbols
  const basicEmojiRegex = /[😀-🙏🌀-🗿🚀-🛿🈁-🈂️⭐-⭕]/u;
  
  return emojiRegex.test(emoji) || basicEmojiRegex.test(emoji) || emoji.length <= 4;
};

// Security: Enhanced logging for security events
export const logSecurityEvent = (event, details, severity = 'info') => {
  const logEntry = {
    timestamp: new Date().toISOString(),
    event,
    details,
    severity,
    userAgent: navigator.userAgent,
    url: window.location.href
  };
  
  // In production, send to secure logging service
  // Security log entry created
  
  // 🛡️ REMOVED LOCALSTORAGE USAGE - Security logs now sent to server
};

// Status badge styling utility
export function getStatusBadgeClass(status) {
  if (!status) return 'bg-gray-400 text-white';
  switch (status.toLowerCase()) {
    case 'completed':
      return 'bg-green-500 text-white';
    case 'processing':
      return 'bg-blue-500 text-white';
    case 'onhold':
      return 'bg-yellow-500 text-white';
    case 'refunded':
      return 'bg-red-500 text-white';
    case 'paid':
      return 'bg-green-600 text-white';
    default:
      return 'bg-gray-400 text-white';
  }
}

// Helper function to safely get text content from message
export const getTextContent = (text) => {
  if (typeof text === 'string') return text;
  if (text && typeof text === 'object' && text.sanitized) return text.sanitized;
  return '';
};

// Helper function to check if two timestamps are near each other (within 5 seconds)
export const isNear = (t1, t2) => {
  return Math.abs(tsToMs(t1) - tsToMs(t2)) < 5000;
};

// Helper function to format file size
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Helper function to get file extension
export const getFileExtension = (filename) => {
  return filename.slice((filename.lastIndexOf('.') - 1 >>> 0) + 2);
};

// Helper function to check if file is image
export const isImageFile = (file) => {
  return file && file.type && file.type.startsWith('image/');
};

// Helper function to check if file is video
export const isVideoFile = (file) => {
  return file && file.type && file.type.startsWith('video/');
};
